import { AUDIO_PLAY_ERROR } from '../../../shared/lib/howlerUtils.ts'

const POLL_MS = 100
const PLAYBACK_CANCELLED = 'Playback cancelled'
export const RENDERER_START_TIMEOUT_MS = 1500
export const RENDERER_STALL_TIMEOUT_MS = 3000

export interface RenderingState {
  clockTime: number
  heartbeats: number
}

export class AudioRendererStalledError extends Error {
  readonly cause: unknown
  constructor(reason: string, details?: unknown) {
    super(AUDIO_PLAY_ERROR)
    this.name = 'AudioRendererStalledError'
    this.cause = { reason, details }
  }
}

/** Count active foreground time, allowing a fresh grace period after a pause. */
class RenderingProgress {
  private previous: RenderingState
  private sampledAt = Date.now()
  private clockMoved = false
  private heartbeatReceived = false
  clockAgeMs = 0
  heartbeatAgeMs = 0

  constructor(private read: () => RenderingState) {
    this.previous = read()
  }

  reset = () => {
    this.previous = this.read()
    this.sampledAt = Date.now()
    this.clockAgeMs = this.heartbeatAgeMs = 0
    this.clockMoved = this.heartbeatReceived = false
  }

  sample(active: boolean) {
    if (!active) {
      this.reset()
      return false
    }
    // A throttled main thread is not evidence that the audio thread stalled.
    const elapsed = Math.max(
      0,
      Math.min(Date.now() - this.sampledAt, POLL_MS * 2),
    )
    this.sampledAt = Date.now()
    const current = this.read()
    this.clockAgeMs += elapsed
    this.heartbeatAgeMs += elapsed
    if (current.clockTime > this.previous.clockTime) {
      this.clockAgeMs = 0
      this.clockMoved = true
    }
    if (current.heartbeats > this.previous.heartbeats) {
      this.heartbeatAgeMs = 0
      this.heartbeatReceived = true
    }
    this.previous = current
    return this.clockMoved && this.heartbeatReceived
  }

  get stalledForMs() {
    return Math.max(this.clockAgeMs, this.heartbeatAgeMs)
  }
}

function renderingExpected(paused: () => boolean) {
  return document.visibilityState !== 'hidden' && !paused()
}

export function waitForAudioForeground(
  signal: AbortSignal,
  paused: () => boolean,
) {
  if (renderingExpected(paused) && !signal.aborted) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    const finish = (error?: unknown) => {
      clearInterval(timer)
      signal.removeEventListener('abort', cancel)
      document.removeEventListener('visibilitychange', check)
      if (error) reject(error)
      else resolve()
    }
    const cancel = () => {
      finish(new DOMException(PLAYBACK_CANCELLED, 'AbortError'))
    }
    const check = () => {
      if (renderingExpected(paused)) finish()
    }
    const timer = setInterval(check, POLL_MS)
    signal.addEventListener('abort', cancel, { once: true })
    document.addEventListener('visibilitychange', check)
    if (signal.aborted) cancel()
  })
}

export function waitForRendering(
  read: () => RenderingState,
  signal: AbortSignal,
  paused: () => boolean,
  finished: Promise<void>,
) {
  const progress = new RenderingProgress(read)
  return new Promise<void>((resolve, reject) => {
    let settled = false
    const finish = (error?: unknown) => {
      if (settled) return
      settled = true
      clearInterval(timer)
      signal.removeEventListener('abort', cancel)
      document.removeEventListener('visibilitychange', progress.reset)
      if (error) reject(error)
      else resolve()
    }
    const cancel = () => {
      finish(new DOMException(PLAYBACK_CANCELLED, 'AbortError'))
    }
    const timer = setInterval(() => {
      if (progress.sample(renderingExpected(paused))) finish()
      else if (progress.stalledForMs >= RENDERER_START_TIMEOUT_MS) {
        finish(
          new AudioRendererStalledError(
            'Audio renderer did not advance during startup',
            read(),
          ),
        )
      }
    }, POLL_MS)
    document.addEventListener('visibilitychange', progress.reset)
    signal.addEventListener('abort', cancel, { once: true })
    void finished.then(() => {
      finish()
    }, finish)
    if (signal.aborted) cancel()
  })
}

export function watchRendering(
  read: () => RenderingState,
  paused: () => boolean,
  recover: () => Promise<void>,
  fail: (error: unknown) => void,
) {
  const progress = new RenderingProgress(read)
  let recovering = false
  let stopped = false
  const timer = setInterval(() => {
    if (recovering || stopped) return
    progress.sample(renderingExpected(paused))
    if (progress.stalledForMs < RENDERER_STALL_TIMEOUT_MS) return
    recovering = true
    void recover().then(
      () => {
        progress.reset()
        recovering = false
      },
      (error: unknown) => {
        if (stopped) return
        if (renderingExpected(paused)) fail(error)
        else {
          progress.reset()
          recovering = false
        }
      },
    )
  }, POLL_MS)
  document.addEventListener('visibilitychange', progress.reset)
  return () => {
    stopped = true
    clearInterval(timer)
    document.removeEventListener('visibilitychange', progress.reset)
  }
}

export function boundedAudioOperation<T>(
  operation: Promise<T>,
  signal: AbortSignal,
  label: string,
) {
  return new Promise<T>((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer)
      signal.removeEventListener('abort', cancel)
    }
    const cancel = () => {
      cleanup()
      reject(new DOMException(PLAYBACK_CANCELLED, 'AbortError'))
    }
    const timer = setTimeout(() => {
      cleanup()
      reject(
        new AudioRendererStalledError(
          `${label} did not settle within ${RENDERER_START_TIMEOUT_MS} ms`,
        ),
      )
    }, RENDERER_START_TIMEOUT_MS)
    signal.addEventListener('abort', cancel, { once: true })
    void operation.then(
      (value) => {
        cleanup()
        resolve(value)
      },
      (error: unknown) => {
        cleanup()
        reject(error)
      },
    )
    if (signal.aborted) cancel()
  })
}
